import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { SalesCategories } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface SalesCategoriesQueryParams {
  businessId: number
}

// Create a unique, descriptive key for this query
const SALES_CATEGORIES_QUERY_KEY = 'sales-categories'

type SalesCategoriesResponse = {
  items: SalesCategories[]
  total: number
  per_page: number
  current_page: number
  total_pages: number
}

/**
 * Fetch sale categories from the API
 * Separating API call logic from the hook for better testability
 */
const fetchSalesCategories = async (): Promise<SalesCategoriesResponse> => {
  const response = await APIServices.get(
    '/administration/salescategory?level=0'
  )
  return response.data
}

/**
 * Custom hook for fetching sale categories
 *
 * @param params - Query parameters including businessId
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useSalesCategoriesQuery = ({
  params,
  options,
}: {
  params: SalesCategoriesQueryParams
  options?: Partial<UseQueryOptions<SalesCategoriesResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<SalesCategoriesResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [SALES_CATEGORIES_QUERY_KEY, params?.businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchSalesCategories(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    salesCategories: queryResult.data?.items,
    isLoadingSalesCategories: queryResult.isLoading,
    isErrorSalesCategories: queryResult.isError,
    refetchSalesCategories: queryResult.refetch,
  }
}

/**
 * Helper function to get the sales categories query key
 * Useful for cache invalidation or prefetching
 */
export const getSalesCategoriesQueryKey = (
  params?: SalesCategoriesQueryParams
) => [SALES_CATEGORIES_QUERY_KEY, params?.businessId]
